import { ValidationError } from '@domain/errors';
import type { AppointmentReasonRef, AppointmentReasonDraft, BusinessTypeDraft, BusinessTypeRef, DirectoryCategoryRef, DirectoryCategoryDraft, OfferCategoryRef, OfferCategoryDraft } from '@domain/entities';
import type { AppointmentReasonRepository, BusinessTypeRepository, DirectoryCategoryRepository, OfferCategoryRepository } from '@domain/repositories';
import type { AccessPolicy } from '../../ports';
import type { UseCase } from '../use-case';

/* -------------------- Motifs de rendez-vous -------------------- */

export class ListAppointmentReasons implements UseCase<void, AppointmentReasonRef[]> {
  constructor(private readonly repo: AppointmentReasonRepository) {}
  execute(): Promise<AppointmentReasonRef[]> { return this.repo.list(); }
}

export interface SaveAppointmentReasonInput {
  id: string | null;
  draft: AppointmentReasonDraft;
}

export class SaveAppointmentReason implements UseCase<SaveAppointmentReasonInput, AppointmentReasonRef> {
  constructor(
    private readonly repo: AppointmentReasonRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ id, draft }: SaveAppointmentReasonInput): Promise<AppointmentReasonRef> {
    this.policy.assertSuperAdmin('gérer les motifs de rendez-vous');
    const fields: Record<string, string> = {};
    if (!draft.label.trim()) fields.label = 'Le libellé est requis';
    if (!draft.slug.trim()) fields.slug = 'Le slug est requis';
    if (Object.keys(fields).length > 0) throw new ValidationError('Formulaire incomplet', fields);
    return id === null ? this.repo.create(draft) : this.repo.update(id, draft);
  }
}

export class ToggleAppointmentReason implements UseCase<{ id: string; currentIsActive: boolean }, AppointmentReasonRef> {
  constructor(
    private readonly repo: AppointmentReasonRepository,
    private readonly policy: AccessPolicy,
  ) {}
  execute({ id, currentIsActive }: { id: string; currentIsActive: boolean }): Promise<AppointmentReasonRef> {
    this.policy.assertSuperAdmin('activer ou désactiver un motif');
    return this.repo.toggleActive(id, currentIsActive);
  }
}

export class DeleteAppointmentReason implements UseCase<string, void> {
  constructor(
    private readonly repo: AppointmentReasonRepository,
    private readonly policy: AccessPolicy,
  ) {}
  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer un motif de rendez-vous');
    await this.repo.remove(id);
  }
}

/* -------------------- Catégories du répertoire -------------------- */

export class ListDirectoryCategories implements UseCase<void, DirectoryCategoryRef[]> {
  constructor(private readonly repo: DirectoryCategoryRepository) {}
  execute(): Promise<DirectoryCategoryRef[]> { return this.repo.list(); }
}

export interface SaveDirectoryCategoryInput {
  id: string | null;
  draft: DirectoryCategoryDraft;
}

export class SaveDirectoryCategory implements UseCase<SaveDirectoryCategoryInput, DirectoryCategoryRef> {
  constructor(
    private readonly repo: DirectoryCategoryRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ id, draft }: SaveDirectoryCategoryInput): Promise<DirectoryCategoryRef> {
    this.policy.assertSuperAdmin('gérer les catégories du répertoire');
    const fields: Record<string, string> = {};
    if (!draft.label.trim()) fields.label = 'Le libellé est requis';
    if (!draft.slug.trim()) fields.slug = 'Le slug est requis';
    if (Object.keys(fields).length > 0) throw new ValidationError('Formulaire incomplet', fields);
    return id === null ? this.repo.create(draft) : this.repo.update(id, draft);
  }
}

export class ToggleDirectoryCategory implements UseCase<{ id: string; currentIsActive: boolean }, DirectoryCategoryRef> {
  constructor(
    private readonly repo: DirectoryCategoryRepository,
    private readonly policy: AccessPolicy,
  ) {}
  execute({ id, currentIsActive }: { id: string; currentIsActive: boolean }): Promise<DirectoryCategoryRef> {
    this.policy.assertSuperAdmin('activer ou désactiver une catégorie');
    return this.repo.toggleActive(id, currentIsActive);
  }
}

export class DeleteDirectoryCategory implements UseCase<string, void> {
  constructor(
    private readonly repo: DirectoryCategoryRepository,
    private readonly policy: AccessPolicy,
  ) {}
  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer une catégorie du répertoire');
    await this.repo.remove(id);
  }
}

/* -------------------- Types d'entreprises -------------------- */

export class ListBusinessTypes implements UseCase<void, BusinessTypeRef[]> {
  constructor(private readonly repo: BusinessTypeRepository) {}
  execute(): Promise<BusinessTypeRef[]> { return this.repo.list(); }
}

export interface SaveBusinessTypeInput {
  id: string | null;
  draft: BusinessTypeDraft;
}

export class SaveBusinessType implements UseCase<SaveBusinessTypeInput, BusinessTypeRef> {
  constructor(
    private readonly repo: BusinessTypeRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ id, draft }: SaveBusinessTypeInput): Promise<BusinessTypeRef> {
    this.policy.assertSuperAdmin('gérer les types d\'entreprises');
    const fields: Record<string, string> = {};
    if (!draft.label.trim()) fields.label = 'Le libellé est requis';
    if (!draft.slug.trim()) fields.slug = 'Le slug est requis';
    if (Object.keys(fields).length > 0) throw new ValidationError('Formulaire incomplet', fields);
    return id === null ? this.repo.create(draft) : this.repo.update(id, draft);
  }
}

export class ToggleBusinessType implements UseCase<{ id: string; currentIsActive: boolean }, BusinessTypeRef> {
  constructor(
    private readonly repo: BusinessTypeRepository,
    private readonly policy: AccessPolicy,
  ) {}
  execute({ id, currentIsActive }: { id: string; currentIsActive: boolean }): Promise<BusinessTypeRef> {
    this.policy.assertSuperAdmin('activer ou désactiver un type d\'entreprise');
    return this.repo.toggleActive(id, currentIsActive);
  }
}

export class DeleteBusinessType implements UseCase<string, void> {
  constructor(
    private readonly repo: BusinessTypeRepository,
    private readonly policy: AccessPolicy,
  ) {}
  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer un type d\'entreprise');
    await this.repo.remove(id);
  }
}

/* -------------------- Catégories d'offres -------------------- */

export class ListOfferCategories implements UseCase<void, OfferCategoryRef[]> {
  constructor(private readonly repo: OfferCategoryRepository) {}
  execute(): Promise<OfferCategoryRef[]> { return this.repo.list(); }
}

export interface SaveOfferCategoryInput {
  id: string | null;
  draft: OfferCategoryDraft;
}

export class SaveOfferCategory implements UseCase<SaveOfferCategoryInput, OfferCategoryRef> {
  constructor(
    private readonly repo: OfferCategoryRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ id, draft }: SaveOfferCategoryInput): Promise<OfferCategoryRef> {
    this.policy.assertSuperAdmin('gérer les catégories d\'offres');
    const fields: Record<string, string> = {};
    if (!draft.label.trim()) fields.label = 'Le libellé est requis';
    if (!draft.slug.trim()) fields.slug = 'Le slug est requis';
    if (Object.keys(fields).length > 0) throw new ValidationError('Formulaire incomplet', fields);
    return id === null ? this.repo.create(draft) : this.repo.update(id, draft);
  }
}

export class ToggleOfferCategory implements UseCase<{ id: string; currentIsActive: boolean }, OfferCategoryRef> {
  constructor(
    private readonly repo: OfferCategoryRepository,
    private readonly policy: AccessPolicy,
  ) {}
  execute({ id, currentIsActive }: { id: string; currentIsActive: boolean }): Promise<OfferCategoryRef> {
    this.policy.assertSuperAdmin('activer ou désactiver une catégorie d\'offres');
    return this.repo.toggleActive(id, currentIsActive);
  }
}

export class DeleteOfferCategory implements UseCase<string, void> {
  constructor(
    private readonly repo: OfferCategoryRepository,
    private readonly policy: AccessPolicy,
  ) {}
  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer une catégorie d\'offres');
    await this.repo.remove(id);
  }
}
